/**
 * Generador del deck "Qué es Habitia y Diferenciales" (Sección Materiales).
 * 3 slides 1920×1080 (qué es / diferenciales / por qué elegirla) portados del
 * handoff de diseño. Devuelve un HTML autocontenido que sirve para preview,
 * presentación fullscreen (navegación con flechas/click) y export a PDF/PNG.
 *
 * Modos:
 *  - "interactive" (default): un slide por vez, escalado al viewport, nav con
 *    teclado/click y sincronización con el editor vía postMessage.
 *  - "export": los 3 slides apilados a tamaño natural, sin script ni
 *    animaciones, para capturar con html2canvas.
 */
import phonePng from "../assets/deck/app-home-framed.png";
import qrPng from "../assets/deck/qr-demo-habitia-blue.png";
import ftBook from "../assets/fonts/FTHabitUnlicensedTrial-Book.otf";
import ftRegular from "../assets/fonts/FTHabitUnlicensedTrial-Regular.otf";
import ftMedium from "../assets/fonts/FTHabitUnlicensedTrial-Medium.otf";

export const TIPO_DECK = "deck_que_es";

// URLs de assets referenciadas en el HTML generado. Para la descarga del HTML
// autocontenido se reemplazan por data-URIs (ver inlineDeckAssets en deckExport).
export const DECK_ASSET_URLS = [phonePng, qrPng, ftBook, ftRegular, ftMedium];

// Contenido editable por el comercial. En los textos, *palabra* se resalta
// (azul en títulos, énfasis en el resto).
export const DEFAULT_DECK = {
  s1: {
    label: "Qué es",
    eyebrow: "¿Qué es Habitia?",
    titulo: "Una administración de edificios con *app* propia.",
    lead: "Con un objetivo claro para el que vive en el edificio: ser más *eficiente, cercana y transparente*. Ves en qué se gasta cada peso desde el celular; reportás un problema con una foto y le seguís el rastro hasta que se arregla; y si un domingo de noche necesitás algo, lo resolvés al instante.",
    chips: ["Más eficiente", "Más cercana", "Más transparente"],
  },
  s2: {
    label: "Cómo se nota",
    eyebrow: "Lo mismo de siempre, mejor hecho",
    titulo: "Tres objetivos, *bajados a la práctica*.",
    subhead: "No es una promesa: es lo que pasa todos los días en la app.",
    cards: [
      { titulo: "Más eficiente", desc: "Todo tu edificio en un solo lugar: pagás los gastos comunes, reservás el salón y ves los documentos sin llamar a nadie. ¿Una filtración? La reportás con una foto y le seguís el estado hasta que se arregla, sin perseguir a nadie." },
      { titulo: "Más cercana", desc: "Un domingo de noche preguntás cuánto debés o reservás el salón y lo resolvés al instante — Habi te responde a cualquier hora. Las novedades del edificio te llegan por WhatsApp, y todo se puede hacer también por ahí: pensado para que hasta los vecinos mayores lo usen sin complicarse.", destacada: true, tag: "Con IA · 24/7" },
      { titulo: "Más transparente", desc: "Ves cada gasto común con su factura desde el celular: sabés exactamente en qué se va tu plata, sin pedírselo a nadie. Tu estado de cuenta, siempre al día." },
    ],
  },
  s3: {
    label: "Por qué elegirla",
    eyebrow: "¿Por qué elegir Habitia?",
    titulo: "Dale a tu edificio la gestión que se merece.",
    razones: [
      { titulo: "Eficiente de verdad", desc: "Todo en un lugar: pagás, reservás amenities y ves los documentos desde el teléfono, y cada problema que reportás queda con seguimiento hasta resolverse." },
      { titulo: "Cerca, cuando lo necesitás", desc: "Habi te responde 24/7 y los avisos del edificio te llegan por WhatsApp. Nunca más quedar esperando al lunes por una consulta." },
      { titulo: "Todo a la vista", desc: "Cada gasto común con su factura y tu estado de cuenta al día. Sabés en qué se va tu plata sin tener que pedírselo a nadie." },
    ],
    demo: {
      kicker: "Probá Habitia",
      titulo: "Accedé a la demo",
      texto: "Escaneá el código y entrá directo a la demo, al instante.",
      cta: "Ver Habitia en acción",
      lugar: "Montevideo, Uruguay",
    },
  },
};

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Escapa y aplica los marcadores: *texto* → <b>texto</b>, saltos de línea → <br>.
const rich = (s) => esc(s).replace(/\*([^*]+)\*/g, "<b>$1</b>").replace(/\n/g, "<br>");

// ── SVGs del prototipo (stroke-based, inline) ──
const CHIP_ICONS = [
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3l7 3v5c0 4.4-3 7.3-7 8.6C8 18.3 5 15.4 5 11V6l7-3z"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 3L5 13h6l-1 8 8-10h-6l1-8z"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>',
];
const CARD_ICONS = [
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.7"><path d="M4 20V9l8-5 8 5v11"/><path d="M9 20v-6h6v6"/><path d="M3 20h18"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.7"><path d="M4 19V5"/><path d="M4 19h16"/><rect x="7" y="11" width="3" height="5"/><rect x="12" y="8" width="3" height="8"/><rect x="17" y="5" width="3" height="11"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.7"><path d="M12 3v3M12 18v3M3 12h3M18 12h3"/><circle cx="12" cy="12" r="4"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.7"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>',
  '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.7"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>',
];
const TAG_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke-width="2"><path d="M12 3v3M12 18v3M3 12h3M18 12h3"/><circle cx="12" cy="12" r="4"/></svg>';
const CHECK_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>';

function topbar(num, label, neg) {
  return `<div class="topbar${neg ? " neg-bar" : ""}">
      <span class="brandmark${neg ? " neg" : ""}">
        <span class="iso${neg ? " neg" : ""}"><span></span><span></span><span></span></span>
        <span class="wordmark">habitia.ai</span>
      </span>
      <span class="pagenum">${num} · ${esc(label)}</span>
    </div>`;
}

function slide1(s1) {
  const chips = (s1.chips || [])
    .map((c, i) => (String(c || "").trim() ? `<span class="chip">${CHIP_ICONS[i % CHIP_ICONS.length]}${esc(c)}</span>` : ""))
    .join("\n        ");
  return `<section class="slide s1" data-label="${esc(s1.label)}">
    ${topbar("01", s1.label, false)}
    <div class="s1__left">
      <span class="eyebrow anim d1">${rich(s1.eyebrow)}</span>
      <h1 class="anim d2">${rich(s1.titulo)}</h1>
      <p class="lead anim d3">${rich(s1.lead)}</p>
      <div class="chips anim d4">
        ${chips}
      </div>
    </div>
    <div class="s1__right">
      <img class="phone anim d2" src="${phonePng}" alt="App de Habitia mostrando el inicio del residente">
    </div>
  </section>`;
}

function slide2(s2) {
  const delays = ["d1", "d2", "d3", "d4", "d4"];
  const cards = (s2.cards || [])
    .map((c, i) => {
      const head = c.destacada
        ? `<span class="tag">${TAG_ICON}${esc(c.tag || "Con IA")}</span>`
        : `<div class="ic">${CARD_ICONS[i % CARD_ICONS.length]}</div>`;
      return `<article class="dcard${c.destacada ? " light" : ""} anim ${delays[i % delays.length]}">
        ${head}
        <h3>${rich(c.titulo)}</h3>
        <p>${rich(c.desc)}</p>
      </article>`;
    })
    .join("\n      ");
  return `<section class="slide s2" data-label="${esc(s2.label)}">
    ${topbar("02", s2.label, false)}
    <span class="eyebrow anim d1">${rich(s2.eyebrow)}</span>
    <h2 class="anim d1">${rich(s2.titulo)}</h2>
    <p class="subhead anim d1">${rich(s2.subhead)}</p>
    <div class="grid">
      ${cards}
    </div>
  </section>`;
}

function slide3(s3, agente) {
  const delays = ["d2", "d3", "d4"];
  const razones = (s3.razones || [])
    .map((r, i) => `<div class="reason anim ${delays[i % delays.length]}">
          <span class="rc">${CHECK_ICON}</span>
          <div><h4>${rich(r.titulo)}</h4><p>${rich(r.desc)}</p></div>
        </div>`)
    .join("\n        ");
  const demo = s3.demo || {};
  const aName = String(agente?.name || "").trim();
  const aEmail = String(agente?.email || "").trim() || "info@habitia.ai";
  const aTel = String(agente?.tel || "").trim();
  const contacto = [
    aName ? `<b>${esc(aName)}</b>` : `<b>${esc(aEmail)}</b>`,
    aName ? esc(aEmail) : "",
    aTel ? esc(aTel) : "",
    esc(demo.lugar || ""),
  ].filter(Boolean).join("<br>");
  return `<section class="slide s3" data-label="${esc(s3.label)}">
    ${topbar("03", s3.label, true)}
    <div class="s3__left">
      <span class="eyebrow neg-eb anim d1">${rich(s3.eyebrow)}</span>
      <h2 class="anim d1">${rich(s3.titulo)}</h2>
      <div class="reasons">
        ${razones}
      </div>
    </div>
    <div class="demo anim d2">
      <span class="kicker">${rich(demo.kicker)}</span>
      <h3>${rich(demo.titulo)}</h3>
      <div class="qrow">
        <img class="qr" src="${qrPng}" alt="Código QR para acceder a la demo de Habitia">
        <p class="qtext">${rich(demo.texto)}<br><br>${contacto}</p>
      </div>
      <span class="cta">${rich(demo.cta)} <span>→</span></span>
    </div>
  </section>`;
}

/**
 * @param {Object} opts
 * @param {Object} opts.deck        Contenido de los slides (shape de DEFAULT_DECK).
 * @param {Object} opts.agente      { name, email, tel } — se muestra en la card de demo.
 * @param {string} opts.mode        "interactive" | "export".
 * @param {number} opts.startSlide  Slide inicial (modo interactive).
 * @returns {string} HTML completo del deck.
 */
export function buildDeckHtml({ deck, agente, mode = "interactive", startSlide = 0 } = {}) {
  const d = deck || DEFAULT_DECK;
  const interactive = mode !== "export";
  const nCards = (d.s2?.cards || []).length || 5;

  const modeCss = interactive
    ? `html, body { margin: 0; height: 100%; background: #0b0d10; overflow: hidden; }
  .stage { position: fixed; inset: 0; overflow: hidden; }
  /* Centrado con translate(-50%,-50%): el slide (1920×1080) es más grande que el
     viewport — centrarlo por layout (grid/flex) no funciona porque el track toma
     el tamaño natural y el scale() lo deja corrido. */
  .scaler { position: absolute; left: 50%; top: 50%; width: 1920px; height: 1080px; transform: translate(-50%, -50%); transform-origin: center center; }
  .scaler > .slide { position: absolute; inset: 0; }
  .scaler > .slide:not([data-deck-active]) { display: none !important; }
  .decknav { position: fixed; right: 18px; bottom: 14px; display: flex; gap: 8px; align-items: center; z-index: 50; font-family: 'FT Habit', system-ui, sans-serif; }
  .decknav button { width: 38px; height: 38px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.25); background: rgba(10,14,20,0.55); color: #fff; font-size: 17px; line-height: 1; cursor: pointer; }
  .decknav .pg { font-size: 13px; color: rgba(255,255,255,0.75); min-width: 40px; text-align: center; }`
    : `html, body { margin: 0; background: #fff; }
  .scaler { width: 1920px; }`;

  const nav = interactive
    ? `<div class="decknav"><button type="button" data-dir="-1" aria-label="Anterior">‹</button><span class="pg">1 / 3</span><button type="button" data-dir="1" aria-label="Siguiente">›</button></div>`
    : "";

  const script = interactive
    ? `<script>
(function () {
  var slides = [].slice.call(document.querySelectorAll('.slide'));
  var pg = document.querySelector('.decknav .pg');
  var i = ${Math.max(0, Math.min(startSlide, 2))};
  function show(n) {
    i = (n % slides.length + slides.length) % slides.length;
    slides.forEach(function (s, j) {
      if (j === i) s.setAttribute('data-deck-active', '');
      else s.removeAttribute('data-deck-active');
    });
    if (pg) pg.textContent = (i + 1) + ' / ' + slides.length;
    try { parent.postMessage({ type: 'habitia-deck-slide', index: i }, '*'); } catch (e) {}
  }
  function fit() {
    var s = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
    document.querySelector('.scaler').style.transform = 'translate(-50%, -50%) scale(' + s + ')';
  }
  window.addEventListener('resize', fit);
  window.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); show(i + 1); }
    if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); show(i - 1); }
    if (e.key === 'Home') show(0);
    if (e.key === 'End') show(slides.length - 1);
  });
  document.querySelector('.stage').addEventListener('click', function () { show(i + 1); });
  [].forEach.call(document.querySelectorAll('.decknav button'), function (b) {
    b.addEventListener('click', function (e) { e.stopPropagation(); show(i + parseInt(b.getAttribute('data-dir'), 10)); });
  });
  window.addEventListener('message', function (e) {
    var m = e && e.data;
    if (!m || typeof m !== 'object') return;
    if (m.type === 'habitia-deck-nav') show(i + (m.dir || 1));
    if (m.type === 'habitia-deck-goto') show(m.index || 0);
  });
  fit(); show(i);
})();
</script>`
    : "";

  const slides = `${slide1(d.s1 || DEFAULT_DECK.s1)}

  ${slide2(d.s2 || DEFAULT_DECK.s2)}

  ${slide3(d.s3 || DEFAULT_DECK.s3, agente)}`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Habitia — Qué es y por qué elegirla</title>
<style>
  @font-face { font-family: 'FT Habit'; font-weight: 350; src: url('${ftBook}') format('opentype'); font-display: swap; }
  @font-face { font-family: 'FT Habit'; font-weight: 400; src: url('${ftRegular}') format('opentype'); font-display: swap; }
  @font-face { font-family: 'FT Habit'; font-weight: 500; src: url('${ftMedium}') format('opentype'); font-display: swap; }

  :root {
    --blue: #0080FF;
    --blue-deep: #0066d6;
    --ink: #0e1014;
    --ink-soft: #454b56;
    --cream: #f0eee9;
    --cream-2: #e7e3da;
    --white: #ffffff;
    --line: rgba(14,16,20,0.11);
  }
  * { box-sizing: border-box; }
  ${modeCss}

  .slide {
    width: 1920px; height: 1080px; position: relative; overflow: hidden;
    font-family: 'FT Habit', system-ui, sans-serif;
    color: var(--ink); background: var(--white);
    -webkit-font-smoothing: antialiased;
  }
  .slide h1, .slide h2, .slide h3 { margin: 0; font-weight: 400; line-height: 1.04; letter-spacing: -0.02em; text-wrap: balance; }
  .slide p { margin: 0; }

  /* --- Brand chrome --- */
  .brandmark { display: inline-flex; align-items: center; gap: 16px; text-decoration: none; }
  .iso { display: inline-flex; align-items: flex-end; gap: 6px; height: 40px; }
  .iso span { display: block; width: 9px; background: var(--blue); border-radius: 2px; }
  .iso span:nth-child(1) { height: 40px; }
  .iso span:nth-child(2) { height: 16px; margin-bottom: 11px; }
  .iso span:nth-child(3) { height: 40px; }
  .iso.neg span { background: #fff; }
  .wordmark { font-size: 38px; font-weight: 500; letter-spacing: -0.01em; color: var(--ink); }
  .neg .wordmark { color: #fff; }

  .topbar { position: absolute; top: 64px; left: 96px; right: 96px; display: flex; align-items: center; justify-content: space-between; z-index: 5; }
  .pagenum { font-size: 22px; font-weight: 500; color: var(--ink-soft); letter-spacing: 0.02em; }
  .neg-bar .pagenum { color: rgba(255,255,255,0.7); }

  .eyebrow { font-size: 26px; font-weight: 500; letter-spacing: -0.005em; color: var(--blue); display: inline-flex; align-items: center; gap: 14px; }
  .eyebrow::before { content: ""; width: 34px; height: 3px; background: var(--blue); border-radius: 2px; }
  .neg-eb { color: #fff; }
  .neg-eb::before { background: #fff; }

  /* =============== SLIDE 1 =============== */
  .s1 { display: grid; grid-template-columns: 1.06fr 0.94fr; }
  .s1__left { padding: 200px 40px 120px 96px; display: flex; flex-direction: column; justify-content: center; }
  .s1 h1 { font-size: 96px; margin-top: 34px; }
  .s1 h1 b { font-weight: 500; color: var(--blue); }
  .s1 .lead { font-size: 32px; line-height: 1.5; color: var(--ink-soft); margin-top: 40px; max-width: 780px; font-weight: 350; }
  .s1 .lead b { font-weight: 500; color: var(--ink); }
  .s1 .chips { display: flex; gap: 14px; margin-top: 52px; flex-wrap: wrap; }
  .chip { display: inline-flex; align-items: center; gap: 12px; font-size: 24px; font-weight: 500; color: var(--ink); background: var(--cream); border: 1px solid var(--line); padding: 15px 26px; border-radius: 999px; }
  .chip svg { width: 24px; height: 24px; color: var(--blue); }

  .s1__right { position: relative; background: linear-gradient(150deg, #0a3f80 0%, var(--blue) 78%); display: grid; place-items: center; overflow: hidden; }
  .s1__right::before { content: ""; position: absolute; width: 900px; height: 900px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,0.16); top: -160px; right: -240px; }
  .s1__right::after { content: ""; position: absolute; width: 620px; height: 620px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,0.14); bottom: -180px; left: -180px; }
  .phone { position: relative; z-index: 2; height: 900px; filter: drop-shadow(0 44px 90px rgba(3,20,50,0.45)); }

  /* =============== SLIDE 2 =============== */
  .s2 { background: var(--cream); padding: 160px 96px 80px; display: flex; flex-direction: column; }
  .s2 h2 { font-size: 68px; margin-top: 20px; }
  .s2 h2 b { font-weight: 500; color: var(--blue); }
  .s2 .subhead { font-size: 30px; font-weight: 350; color: var(--ink-soft); margin-top: 16px; }
  .s2 .grid { display: grid; grid-template-columns: repeat(${nCards}, 1fr); gap: 20px; margin-top: 52px; flex: 1; }
  .dcard { background: var(--blue); color: #fff; border-radius: 24px; padding: 46px 34px; display: flex; flex-direction: column; }
  .dcard.light { background: #fff; border: 1px solid var(--line); }
  .dcard .ic { width: 72px; height: 72px; border-radius: 18px; background: rgba(255,255,255,0.15); display: grid; place-items: center; margin-bottom: 30px; }
  .dcard .ic svg { width: 38px; height: 38px; stroke: #fff; }
  .dcard.light .ic { background: rgba(0,128,255,0.1); }
  .dcard.light .ic svg { stroke: var(--blue); }
  .dcard h3 { font-size: 32px; font-weight: 500; color: #fff; line-height: 1.12; }
  .dcard.light h3 { color: var(--blue); }
  .dcard p { font-size: 24px; line-height: 1.44; color: rgba(255,255,255,0.88); margin-top: 18px; font-weight: 350; }
  .dcard.light p { color: var(--ink-soft); }
  .dcard .tag { display: inline-flex; width: fit-content; align-items: center; gap: 9px; font-size: 24px; font-weight: 500; background: rgba(0,128,255,0.12); color: var(--blue); padding: 8px 16px; border-radius: 999px; margin-bottom: 24px; }
  .dcard .tag svg { stroke: var(--blue); }

  /* =============== SLIDE 3 =============== */
  .s3 { background: linear-gradient(155deg, #063a78 0%, var(--blue) 70%); color: #fff; padding: 190px 96px 110px; display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 80px; align-items: center; }
  .s3__left h2 { font-size: 86px; color: #fff; margin-top: 30px; }
  .s3 .reasons { display: flex; flex-direction: column; gap: 30px; margin-top: 64px; }
  .reason { display: flex; gap: 26px; align-items: flex-start; }
  .reason .rc { flex: none; width: 56px; height: 56px; border-radius: 50%; background: rgba(255,255,255,0.16); display: grid; place-items: center; }
  .reason .rc svg { width: 30px; height: 30px; stroke: #fff; }
  .reason h4 { margin: 0; font-size: 34px; font-weight: 500; color: #fff; letter-spacing: -0.01em; }
  .reason p { font-size: 24px; line-height: 1.45; color: rgba(255,255,255,0.82); margin-top: 6px; font-weight: 350; }
  .demo { background: #fff; border-radius: 30px; padding: 56px 52px; color: var(--ink); box-shadow: 0 40px 110px rgba(3,18,45,0.4); }
  .demo .kicker { font-size: 24px; font-weight: 500; color: var(--blue); }
  .demo h3 { font-size: 48px; color: var(--ink); margin-top: 14px; line-height: 1.05; }
  .demo .qrow { display: flex; align-items: center; gap: 30px; margin-top: 40px; }
  .demo .qr { width: 190px; height: 190px; border-radius: 18px; border: 1px solid var(--line); }
  .demo .qtext { font-size: 24px; line-height: 1.5; color: var(--ink-soft); font-weight: 350; }
  .demo .qtext b { color: var(--ink); font-weight: 500; }
  .demo .cta { margin-top: 40px; display: inline-flex; align-items: center; gap: 14px; background: var(--blue); color: #fff; font-size: 26px; font-weight: 500; padding: 20px 40px; border-radius: 999px; }

  @media (prefers-reduced-motion: no-preference) {
    [data-deck-active] .anim { opacity: 0; transform: translateY(30px); animation: rise .7s cubic-bezier(.2,.7,.3,1) forwards; }
    [data-deck-active] .anim.d1 { animation-delay: .08s; }
    [data-deck-active] .anim.d2 { animation-delay: .16s; }
    [data-deck-active] .anim.d3 { animation-delay: .24s; }
    [data-deck-active] .anim.d4 { animation-delay: .32s; }
    @keyframes rise { to { opacity: 1; transform: none; } }
  }
</style>
</head>
<body>
${interactive ? `<div class="stage"><div class="scaler">
  ${slides}
</div></div>
${nav}` : `<div class="scaler">
  ${slides}
</div>`}
${script}
</body>
</html>`;
}
